import { useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconBrandADManager } from '@components/icons/brand-ad-manager';
import { IconRestart } from '@components/icons/restart';
import { IconTeam } from '@components/icons/team';
import { RelativeSpinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { abbreviateDate } from '@utils/date';

import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';

import { adManagerGetApplication, ApplicationResponse, isCreatedUserResponse } from './api';

type ApplicationParams = {
  jobID: string;
  applicationID: string;
};

export const ADManagerApplicationApp = () => {
  const { t } = useStore();
  const { applicationID, jobID } = useParams() as ApplicationParams;
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();
  const applicationQuery = useQuery({
    queryKey: ['ad-manager-application', applicationID],
    queryFn: () => adManagerGetApplication(applicationID),
  });

  if (applicationQuery.isLoading) {
    return <RelativeSpinner />;
  }
  if (!applicationQuery.data) {
    return null;
  }
  return (
    <Stack space={6}>
      <Stack space={0} orientation="horizontal" align="center" justify="space-between">
        <Stack space={2} orientation="horizontal" align="center">
          <IconBrandADManager size={24} />
          <Text font="semibold14" text={t('integrations.admanager.title')} />
        </Stack>
        {hasRolePermission('application:send-admanager') &&
          !isCreatedUserResponse(applicationQuery.data) && (
            <Button
              iconLeft={<IconRestart />}
              variant="ghost"
              text={t('integrations.admanager.application.button.create')}
              onClick={() =>
                showModal('INTEGRATION_ADMANAGER_CREATE_USER', {
                  applicationID,
                  jobID,
                })
              }
            />
          )}
      </Stack>
      <Content data={applicationQuery.data} />
    </Stack>
  );
};

const Content = (props: { data: ApplicationResponse }) => {
  const { t, lang } = useStore();
  if (!isCreatedUserResponse(props.data)) {
    return (
      <Card variant="gray" space={6}>
        <Stack space={4} orientation="horizontal">
          <IconTeam color="purple80" />
          <Text color="purple80" text={t('integrations.admanager.application.notcreated')} />
        </Stack>
      </Card>
    );
  }
  return (
    <Card space={6}>
      <Stack space={4}>
        <Stack space={0}>
          <Text font="semibold14" text={t('integrations.admanager.application.username.label')} />
          <Text text={props.data.results.USER_EMAIL} />
        </Stack>
        <Stack space={0}>
          <Text
            font="semibold14"
            text={t('integrations.admanager.application.generatedby.label')}
          />
          <Text
            text={`${props.data.createdBy} • ${abbreviateDate(props.data.dateSent, lang, 'date')}`}
          />
        </Stack>
      </Stack>
    </Card>
  );
};
